import { Injectable } from '@angular/core';
import { APP_CONFIGS, AppConfig, buildAppUrl } from './app-navigation.config';
import {
  ACCESS_TOKEN_COOKIE,
  REFRESH_TOKEN_COOKIE,
  REFRESH_TOKEN_EXPIRES_COOKIE,
  TOKEN_EXPIRES_COOKIE,
  EMAIL_COOKIE,
  LANGUAGE_COOKIE,
  AUTH_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_KEY,
} from '@dental-ui/shared/consts';

@Injectable({
  providedIn: 'root',
})
export class AppNavigationService {
  /**
   * Tüm mevcut uygulamaları getirir (sadece etkin olanlar)
   */
  getAvailableApps(): AppConfig[] {
    return Object.values(APP_CONFIGS).filter((app) => app.enabled);
  }

  /**
   * Tüm uygulamaları getirir (devre dışı olanlar dahil)
   */
  getAllApps(): AppConfig[] {
    return Object.values(APP_CONFIGS);
  }

  /**
   * İsme göre uygulama konfigürasyonunu getirir
   */
  getAppConfig(appName: string): AppConfig | undefined {
    return APP_CONFIGS[appName];
  }

  /**
   * Cookie'den değer okur
   */
  private getCookie(name: string): string | null {
    if (typeof document === 'undefined') {
      return null;
    }
    const cookies = document.cookie?.split('; ') ?? [];
    for (const cookie of cookies) {
      if (cookie.startsWith(`${name}=`)) {
        return decodeURIComponent(cookie.substring(name.length + 1));
      }
    }
    return null;
  }

  private getStoredValue(key: string): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  }

  /** Cookie yoksa user JSON'dan auth alanlarını oku (lab→cad geçişinde expiry kaybını önler). */
  private getStoredUserField(field: string): string | null {
    const raw = this.getStoredValue(USER_KEY);
    if (!raw) {
      return null;
    }
    try {
      const user = JSON.parse(raw) as Record<string, unknown>;
      const value = user?.[field];
      if (value == null || value === '') {
        return null;
      }
      return String(value);
    } catch {
      return null;
    }
  }

  /**
   * Cookie'lerden auth bilgilerini alıp URL parametrelerine ekler
   * Cookie'de yoksa localStorage ve sessionStorage'dan kontrol eder
   */
  private addAuthParamsToUrl(url: string): string {
    const accessToken =
      this.getCookie(ACCESS_TOKEN_COOKIE) ||
      this.getStoredValue(AUTH_TOKEN_KEY);

    const refreshToken =
      this.getCookie(REFRESH_TOKEN_COOKIE) ||
      this.getStoredValue(REFRESH_TOKEN_KEY);

    const refreshTokenExpired =
      this.getCookie(REFRESH_TOKEN_EXPIRES_COOKIE) ||
      this.getStoredUserField('refreshTokenExpired');

    const expiration =
      this.getCookie(TOKEN_EXPIRES_COOKIE) ||
      this.getStoredUserField('expiration');

    const email =
      this.getCookie(EMAIL_COOKIE) ||
      this.getStoredUserField('email');

    const language = this.getCookie(LANGUAGE_COOKIE);

    if (!accessToken || !refreshToken) {
      return url; // Auth bilgileri yoksa parametre ekleme
    }

    const params = new URLSearchParams();
    params.set('accessToken', accessToken);
    params.set('refreshToken', refreshToken);
    if (refreshTokenExpired) params.set('refreshTokenExpired', refreshTokenExpired);
    if (expiration) params.set('expiration', expiration);
    if (email) params.set('email', email);
    if (language) params.set('language', language);

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${params.toString()}`;
  }

  /**
   * Başka bir uygulamaya yönlendirir
   * @param appName - Yönlendirilecek uygulamanın adı
   * @param path - Uygulama içindeki opsiyonel path
   * @param openInNewTab - Yeni sekmede açılıp açılmayacağı (varsayılan: false)
   */
  navigateToApp(appName: string, path = '', openInNewTab = false): void {
    const config = this.getAppConfig(appName);
    
    if (!config) {
      console.error(`App ${appName} not found`);
      return;
    }
    
    if (!config.enabled) {
      console.warn(`App ${appName} is not enabled`);
      return;
    }
    
    let url = buildAppUrl(appName, path);
    
    // Add auth parameters from cookies to URL (except for landing app)
    // Landing app doesn't need auth cookies in URL
    if (appName !== 'landing') {
      url = this.addAuthParamsToUrl(url);
    }
    
    // Uygulamaya yönlendir (varsayılan olarak aynı sekme, belirtilirse yeni sekme)
    // Base domain'e ayarlanmış cookie'ler subdomain'ler arasında paylaşılacaktır
    if (openInNewTab) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = url;
    }
  }

  /**
   * Uygulamanın etkin olup olmadığını kontrol eder
   */
  isAppEnabled(appName: string): boolean {
    const config = this.getAppConfig(appName);
    return config?.enabled ?? false;
  }
}

